// The nine universal states (v1.1 §11.4), as components.
//
// WHY THESE EXIST AS A SHARED SET
// ===============================
// Sixteen tabs are still unbuilt. Every one of them will need to say "loading",
// "nothing here", "the server refused you" and "this broke", and if each one
// answers that question locally we get sixteen slightly different opinions about
// what empty looks like and sixteen places to forget the 403. `routes/operations`
// already solved this for five tabs — `useEndpoint.js` names four outcomes and
// `Panel.jsx` renders them honestly. This module is that pattern generalised so
// the other tabs inherit it instead of reinventing it.
//
// THE HONESTY RULE IS THE WHOLE POINT
// ===================================
// Refused is not empty, and neither one is broken. On an operations screen those
// three mean opposite things: "you may not see this", "there is genuinely nothing
// happening", and "we failed to find out". A surface that renders all three as a
// blank panel is lying in the most expensive direction — it reads as calm. So
// each is a distinct component with distinct copy, and none of them is the
// default.
//
// CLASS NAMES COME FROM THE DESIGN SYSTEM, NOT FROM HERE
// ======================================================
// `design/system/DESIGN_SYSTEM.md` §11.4 already names these: `.skeleton` for
// loading, `.state` for empty, `.state.error`, `.state.offline`, `.state.denied`,
// and `.state-tag` with `running` / `working` / `approval` / `success`. Those are
// used verbatim below. The design system session owns the actual styling in
// `cipher.css`; using its vocabulary means that work restyles these components
// without touching them, and without a rename landing in both branches at once.
//
// STRUCTURE ONLY. NO ADOPTION.
// ============================
// Nothing in the existing tabs imports this yet. Adoption is a separate
// increment, deliberately, so that this one cannot regress a live surface.
import React from "react";

import "./states.css";

/* ── the five surface states ──────────────────────────────────────────── */

/**
 * Loading. A calm shimmer, not a spinner — §11.4 calls for the skeleton so the
 * surface keeps its shape while it fills.
 *
 * `lines` draws more than one bar for surfaces that are visibly a list. `label`
 * is read by screen readers; a bare animated div announces nothing.
 */
export function Loading({ label = "Loading", lines = 1 }) {
  return (
    <div className="state-loading" role="status" aria-label={label}>
      {Array.from({ length: Math.max(1, lines) }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

/**
 * Empty. The surface worked, the answer is genuinely nothing.
 *
 * §11.4 calls empty the weakest of the nine and asks for icon + reason + primary
 * action. `detail` is the reason and `action` is that primary action; both are
 * optional because a reason invented to fill the slot is worse than no reason.
 */
export function Empty({ title = "Nothing here yet", detail, action }) {
  return (
    <div className="state state-empty" role="status">
      <div className="state-empty-title">{title}</div>
      {detail ? <div className="state-empty-detail dim">{detail}</div> : null}
      {action ? <div className="state-empty-action">{action}</div> : null}
    </div>
  );
}

/**
 * Error. We tried and could not find out.
 *
 * `role="alert"` rather than `status` — this is the one state of the five that
 * should interrupt a screen reader, because unlike the others it means the user
 * is looking at a surface that is not telling them the truth about the system.
 */
export function ErrorState({ title = "Could not load", detail, error, retry }) {
  return (
    <div className="state error state-error" role="alert">
      <div className="state-empty-title">{title}</div>
      <div className="state-empty-detail dim">
        {detail ||
          "This is reporting the failure rather than showing a blank that would read as calm."}
        {error ? ` (${error})` : null}
      </div>
      {retry ? (
        <div className="state-empty-action">
          <button type="button" className="btn" onClick={retry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Offline / degraded. We could not reach the server at all, or reached a part of
 * it that is answering in a reduced way.
 *
 * Distinct from Error on purpose: an unreachable backend is a different user
 * action ("wait, check the connection") from a backend that answered with a
 * failure ("this request is wrong or this thing is broken"). `useEndpoint.js`
 * does not currently separate the two — it reports `unreachable` inside FAILED —
 * and that is noted rather than changed here, because changing it would edit a
 * live Operations surface.
 */
export function Offline({ title = "Not reachable", detail, retry }) {
  return (
    <div className="state offline state-offline" role="status">
      <div className="state-empty-title">{title}</div>
      <div className="state-empty-detail dim">
        {detail || "The server did not answer. Nothing here is out of date — it is unknown."}
      </div>
      {retry ? (
        <div className="state-empty-action">
          <button type="button" className="btn" onClick={retry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Permission denied. The server refused, and saying so is the honest outcome.
 *
 * R5: the client does not gate, the server does. This state exists so that a
 * refusal is visible as a refusal. It deliberately does not offer a retry — the
 * answer will not change by asking again, and a retry button would imply it
 * might.
 */
export function Denied({ title = "Not available to this account", detail, error }) {
  return (
    <div className="state denied state-denied" role="status">
      <div className="state-empty-title">{title}</div>
      <div className="state-empty-detail dim">
        {detail ||
          "The server refused this request. Navigation shows it; the data is gated where it should be."}
        {error ? ` (${error})` : null}
      </div>
    </div>
  );
}

/* ── the four process tags ────────────────────────────────────────────── */

/**
 * The process tags are inline, not surfaces. They sit next to a thing that is
 * happening — a row, a card header, a panel title — and say what kind of
 * happening it is. §11.4 fixes the colour meanings (running cyan, working
 * magenta, approval amber, success green) so that "the system is doing
 * something" looks identical in Operations, EA, Brain and Workstation.
 */
function Tag({ kind, children, label }) {
  return (
    <span className={`state-tag ${kind}`} role="status" aria-label={label}>
      <span className="state-tag-dot" aria-hidden="true" />
      <span className="state-tag-text">{children}</span>
    </span>
  );
}

/** A workflow is running — deterministic, system-driven work. */
export function Running({ children = "Running" }) {
  return <Tag kind="running" label={`Workflow ${children}`}>{children}</Tag>;
}

/** An agent is working — non-deterministic work, which is a different promise. */
export function Working({ children = "Working" }) {
  return <Tag kind="working" label={`Agent ${children}`}>{children}</Tag>;
}

/** Stopped, waiting on a human decision. Nothing progresses until someone acts. */
export function ApprovalRequired({ children = "Approval required" }) {
  return <Tag kind="approval" label={children}>{children}</Tag>;
}

/** Finished, and finished well. */
export function Success({ children = "Success" }) {
  return <Tag kind="success" label={children}>{children}</Tag>;
}

/* ── dispatch ─────────────────────────────────────────────────────────── */

/**
 * The status vocabulary `routes/operations/useEndpoint.js` already uses, repeated
 * here rather than imported.
 *
 * Importing it would make this shared module depend on one area's internals, and
 * the dependency points the wrong way — Operations should eventually depend on
 * this, not the reverse. The values are identical strings on purpose so that a
 * later consolidation is a deletion rather than a translation.
 */
export const IDLE = "idle";
export const LOADING = "loading";
export const READY = "ready";
export const REFUSED = "refused";
export const FAILED = "failed";
export const OFFLINE = "offline";

/**
 * Render the right state for a status, or `children` when there is data.
 *
 * This is what makes adoption a small edit later: a surface wraps its body in
 * `<SurfaceState status={...} empty={...}>` instead of writing its own four-way
 * conditional. It is not wired into anything yet.
 *
 * `empty` is a boolean the caller computes, not something inferred here — only
 * the surface knows whether an empty array means "nothing happening" or "one
 * section of a payload that is legitimately absent".
 */
export function SurfaceState({
  status,
  empty = false,
  error,
  retry,
  emptyTitle,
  emptyDetail,
  loadingLabel,
  loadingLines,
  children,
}) {
  if (status === IDLE) return null;
  if (status === LOADING) {
    return <Loading label={loadingLabel} lines={loadingLines} />;
  }
  if (status === REFUSED) return <Denied error={error} />;
  if (status === OFFLINE) return <Offline retry={retry} />;
  if (status === FAILED) return <ErrorState error={error} retry={retry} />;
  if (status === READY && empty) {
    return <Empty title={emptyTitle} detail={emptyDetail} />;
  }
  return children ?? null;
}
